import axios from 'axios';

class ApiService {
  constructor() {
    this.csrfToken = null;
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      }
    });

    // Request interceptor to add tokens
    this.api.interceptors.request.use(
      async (config) => {
        // Add Bearer token if available
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add CSRF token for non-GET requests
        if (!['get', 'head', 'options'].includes(config.method?.toLowerCase())) {
          await this.ensureCSRFToken();
          if (this.csrfToken) {
            config.headers['X-CSRF-Token'] = this.csrfToken;
          }
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle CSRF errors
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 403 && 
            error.response?.data?.message === 'Invalid CSRF token') {
          // CSRF token is invalid, get a new one and retry
          this.csrfToken = null;
          await this.ensureCSRFToken();
          
          // Retry the original request with new CSRF token
          const originalRequest = error.config;
          if (!originalRequest._retry) {
            originalRequest._retry = true;
            if (this.csrfToken) {
              originalRequest.headers['X-CSRF-Token'] = this.csrfToken;
            }
            return this.api.request(originalRequest);
          }
        }
        
        return Promise.reject(error);
      }
    );
  }

  // Get CSRF token from backend
  async getCSRFToken() {
    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await axios.get(`${baseURL}/csrf-token`, {
        withCredentials: true
      });
      
      if (response.data.success && response.data.csrfToken) {
        this.csrfToken = response.data.csrfToken;
        return this.csrfToken;
      }
    } catch (error) {
      console.error('Error getting CSRF token:', error);
      
      // Check if this is a port mismatch issue
      if (error.code === 'ECONNREFUSED' || error.message.includes('Network Error')) {
        const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        console.error(`❌ Cannot connect to API at: ${baseURL}`);
        console.error('💡 Possible fixes:');
        console.error('   1. Check if backend server is running');
        console.error('   2. Verify REACT_APP_API_URL in .env.local');
        console.error('   3. Clear browser cache and reload');
      }
    }
    return null;
  }

  // Ensure we have a valid CSRF token
  async ensureCSRFToken() {
    if (!this.csrfToken) {
      await this.getCSRFToken();
    }
    return this.csrfToken;
  }

  // Clear CSRF token (useful for logout)
  clearCSRFToken() {
    this.csrfToken = null;
  }

  // Generic API methods
  async get(url, config = {}) {
    const response = await this.api.get(url, config);
    return response.data;
  }

  async post(url, data = {}, config = {}) {
    const response = await this.api.post(url, data, config);
    return response.data;
  }

  async put(url, data = {}, config = {}) {
    const response = await this.api.put(url, data, config);
    return response.data;
  }

  async delete(url, config = {}) {
    const response = await this.api.delete(url, config);
    return response.data;
  }

  // Auth specific methods
  async login(credentials) {
    return this.post('/auth/login', credentials);
  }

  async register(userData) {
    return this.post('/auth/register', userData);
  }

  async logout() {
    const result = await this.post('/auth/logout');
    this.clearCSRFToken();
    return result;
  }

  async forgotPassword(email) {
    return this.post('/auth/forgot-password', { email });
  }

  async resetPassword(token, password) {
    return this.post('/auth/reset-password', { token, password });
  }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService;